import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { MoreHorizontal, Edit, Trash2, Eye } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import type { SmsTemplate } from "@/types/sms-queue";
import { previewSmsMessage } from "@/utils/smsUtils";

interface SmsTemplateCardProps {
  template: SmsTemplate;
  onSetDefault: (id: string) => void;
  onEdit: (template: SmsTemplate) => void;
  onDelete: (id: string) => void;
  onPreview: (template: SmsTemplate) => void;
}

export const SmsTemplateCard = ({ 
  template, 
  onSetDefault, 
  onEdit, 
  onDelete, 
  onPreview 
}: SmsTemplateCardProps) => {
  const previewText = previewSmsMessage(template.message);
  
  return (
    <Card className={`transition-all ${template.is_default ? 'border-primary bg-primary/5' : ''}`}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CardTitle className="text-lg">{template.name}</CardTitle>
            {template.is_default && (
              <Badge variant="default" className="text-xs">
                Padrão
              </Badge>
            )}
          </div>
          
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">
                {template.is_default ? "Ativo" : "Inativo"}
              </span>
              <Switch
                checked={template.is_default}
                onCheckedChange={() => onSetDefault(template.id)}
              />
            </div>
            
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => onPreview(template)}>
                  <Eye className="h-4 w-4 mr-2" />
                  Ver Preview
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onEdit(template)}>
                  <Edit className="h-4 w-4 mr-2" />
                  Editar
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={() => onDelete(template.id)}
                  className="text-destructive"
                  disabled={template.is_default}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Excluir
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </CardHeader>
      
      <CardContent>
        <div className="space-y-3">
          <div className="p-3 bg-muted rounded-md">
            <p className="text-sm text-muted-foreground mb-1">Mensagem:</p>
            <p className="text-sm line-clamp-2">{template.message}</p>
          </div>
          
          <div className="p-3 bg-background border rounded-md">
            <p className="text-sm text-muted-foreground mb-1">Preview:</p>
            <p className="text-sm text-foreground">{previewText}</p>
            <p className="text-xs text-muted-foreground mt-1">
              {previewText.length}/160 caracteres
            </p>
          </div>
          
          <div className="flex flex-wrap gap-1">
            {template.variables.map((variable) => (
              <Badge key={variable} variant="outline" className="text-xs">
                {`{${variable}}`}
              </Badge>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};